import { useEffect } from 'react';

const TRACKS = '.bo-tabbar--pills, .ui-tabband';
const ACTIVE = '.bo-tabbar__tab.is-active, .ui-tabband__item[aria-current="page"]';

/** Scrolls the track itself (never the page) so its active tab is fully visible. */
function revealActive(track: Element): void {
  const active = track.querySelector<HTMLElement>(ACTIVE);
  if (!active || !(track instanceof HTMLElement) || track.scrollWidth <= track.clientWidth) return;
  const trackBox = track.getBoundingClientRect();
  const box = active.getBoundingClientRect();
  if (box.left < trackBox.left) track.scrollLeft -= trackBox.left - box.left + 16;
  else if (box.right > trackBox.right) track.scrollLeft += box.right - trackBox.right + 16;
}

/**
 * Tabs sit in one sideways-scrolling line (style guide 2.61, human feedback
 * 2026-10-01): whenever a tab row appears or its active tab changes, the active
 * tab is scrolled into view inside its track. One observer for every tab row
 * of the app instead of a ref in each screen.
 */
export function useActiveTabInView(): void {
  useEffect(() => {
    const root = document.getElementById('root') ?? document.body;
    let frame = 0;
    const sweep = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => root.querySelectorAll(TRACKS).forEach(revealActive));
    };
    const observer = new MutationObserver(sweep);
    observer.observe(root, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'aria-current'] });
    sweep();
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, []);
}
